<template>
  <div class="widget__content-center">
    <template v-if="time?.name === 'Total'">
      <div class="widget__header-text">Showing <span>average dwell times</span> per zone</div>

      <div class="dwell-average">
        <div class="dwell-time__icon">
          <IconTime />
        </div>
        <div class="widget__label widget__label-thin">Average Zone Dwell Total</div>
        <div class="widget__bignumber">
          {{ zoneTotalCount }}
        </div>
      </div>

      <div v-if="zoneTotal.length" class="chart-wrapper">
        <apexchart
          :key="time"
          type="bar"
          width="100%"
          :options="chartOptions"
          :series="zoneTotal"
        />
      </div>
    </template>

    <template v-if="time?.name === 'Day'">
      <div class="widget__header-text">Showing <span>daily average dwell times</span> per zone</div>

      <div class="dwell-average">
        <div class="dwell-time__icon">
          <IconTime />
        </div>
        <div class="widget__label widget__label-thin">Average Zone Dwell Total</div>
        <div class="widget__bignumber">
          {{ zoneDailyCount }}
        </div>
      </div>

      <div v-if="zoneDaily.length" class="chart-wrapper">
        <apexchart
          :key="time"
          type="bar"
          width="100%"
          :options="chartOptions"
          :series="zoneDaily"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { barColors } from '@/utils/chartColors';
import { secToMinHr, secToMin, secToMinDec, secToMinHrSec } from '@/utils/formatTimeSpent';
import type { WidgetFilter, WidgetSetting, WidgetTypeEngagedVisitDwell } from '@/types/Widget';

import IconTime from '@/icons/analytics/IconTime.svg?component';

import type { BaseChartOptions } from '@/utils/chartSettings';
import { getChartExportSettings, getDefaultChartOptions } from '@/utils/chartSettings';

const props = defineProps<{
  widget: WidgetTypeEngagedVisitDwell;
  area?: WidgetFilter;
  time?: WidgetFilter;
  zone?: WidgetSetting;
  day?: WidgetSetting;
}>();

watch(props, () => {
  updateCategories();
});

const defaultChartOptions = getDefaultChartOptions(props.widget.code_name);

const chartOptions = ref<BaseChartOptions>({
  ...defaultChartOptions.value,
  colors: barColors,
  chart: {
    ...defaultChartOptions.value.chart,
    type: 'bar',
    id: 'meshh-chart-repeat-visits',
    toolbar: getChartExportSettings(
      props.widget.name,
      'Zone Average',
      props.time?.name || '',
      props.zone?.title || '',
      '',
      props.day?.id || '',
    ),
  },
  plotOptions: {
    bar: {
      borderRadius: 4,
      borderRadiusApplication: 'end',
      horizontal: true,
    },
  },
  dataLabels: {
    enabled: false,
  },
  xaxis: {
    categories: [],
    title: {
      text: 'Dwell Time (mins)',
    },
  },
  tooltip: {
    y: {
      formatter: (val: string) => {
        const min = parseFloat(val);
        const sec = 60 * min;
        return secToMinHrSec(sec).text;
      },
    },
  },
});

const zoneTotalCount = computed(() => {
  let totalTime = `0m`;

  const zoneAvgData = props.widget.data['Zone Average'];
  if (zoneAvgData) {
    const data = zoneAvgData['Total'];
    let total = 0;

    Object.keys(data).forEach((key) => {
      total += data[key];
    });

    const average = total / Object.keys(data).length;
    const translated = secToMinHr(average);
    totalTime = translated.text;
  }
  return totalTime;
});

const zoneTotal = computed(() => {
  const zoneAvgData = props.widget.data['Zone Average'];

  if (zoneAvgData) {
    const data = zoneAvgData['Total'];
    const series: number[] = [];

    Object.keys(data).forEach((key) => {
      series.push(Math.floor(secToMin(data[key])));
    });

    return [{ name: 'Dwell time', data: series }];
  }

  return [];
});

const zoneDailyCount = computed(() => {
  let totalTime = `0m`;

  const zoneAvgData = props.widget.data['Zone Average'];
  if (zoneAvgData) {
    const daily = zoneAvgData['Day'];
    const dayId = props.day?.id;

    if (daily && dayId) {
      const data = daily[dayId];
      let total = 0;

      Object.keys(data).forEach((key) => {
        total += data[key];
      });

      const average = total / Object.keys(data).length;
      const translated = secToMinHr(average);
      totalTime = translated.text;
    }
  }

  return totalTime;
});

const zoneDaily = computed(() => {
  const zoneAvgData = props.widget.data['Zone Average'];

  if (zoneAvgData) {
    const daily = zoneAvgData['Day'];
    const dayId = props.day?.id;

    if (daily && dayId) {
      const data = daily[dayId];
      const series: string[] = [];

      Object.keys(data).forEach((key) => {
        series.push(secToMinDec(data[key]));
      });

      return [{ name: 'Dwell time', data: series }];
    }
  }

  return [];
});

const updateCategories = () => {
  const zoneAvgData = props.widget.data['Zone Average'];
  if (!zoneAvgData) {
    return;
  }

  let data = zoneAvgData['Total'];

  if (props.time?.name === 'Day') {
    const daily = zoneAvgData['Day'];
    const dayId = props.day?.id;

    if (daily && dayId) {
      data = daily[dayId];
    }
  }

  const categories: string[] = [];

  if (data) {
    Object.keys(data).forEach((key) => {
      const zoneId = key;
      const zones = props.widget.filters.area.find((area) => area.name === 'Zone Average');
      const chosenZone = zones?.series?.find((zone) => zone.id === zoneId);

      if (chosenZone) {
        const zoneName = chosenZone.title;
        if (zoneName) {
          categories.push(zoneName);
        }
      }
    });
  }

  if (chartOptions.value.xaxis) {
    chartOptions.value.xaxis.categories = categories;
  }
};

updateCategories();
</script>
